import React from 'react';
import { useAuth, AuthStore } from '../hooks/useAuth';

interface AuthGuardProps {
  token: string;
  children: (store: AuthStore) => React.ReactNode;
}

const Spinner: React.FC = () => (
  <div
    style={{
      width: 28,
      height: 28,
      border: '3px solid #e5e7eb',
      borderTop: '3px solid #6366f1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }}
  />
);

export const AuthGuard: React.FC<AuthGuardProps> = ({ token, children }) => {
  const { isAuthenticated, isLoading, error, store } = useAuth(token);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A]">
        <div className="text-center max-w-md p-10 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
          <div className="flex justify-center mb-6">
            <Spinner />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Verifying access…</h1>
          <p className="text-white/60 text-sm">Please wait while we authenticate your session.</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error || !isAuthenticated || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A]">
        <div className="text-center max-w-md p-10 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-red-500/15 flex items-center justify-center text-red-400 text-2xl">
            !
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-white/60 text-sm">{error || 'Unable to verify store access.'}</p>
        </div>
      </div>
    );
  }

  return <>{children(store)}</>;
};
